import { useState, type ComponentProps } from 'react';
import {
  Box,
  Card,
  CardContent,
  Chip,
  Button,
  FormControlLabel,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import {
  Loading,
  useDataProvider,
  useGetIdentity,
  useGetList,
  useNotify,
  useRefresh,
} from 'react-admin';
import { useRegion } from '../auth/RegionContext';
import { RequireAccess } from '../auth/RequireAccess';
import { useAccessContext } from '../auth/useAccessContext';
import { hasAccess } from '../auth/permissions';
import { JustifiedAction } from '../actions/JustifiedAction';
import { CrossRegionConfirm } from '../actions/CrossRegionConfirm';
import type {
  AdminDataProvider,
  Currency,
  FeatureFlag,
  PlusMarketingFlag,
  PlusMarketingState,
  SystemSetting,
} from '../dataProvider';
import {
  CURRENCY_LABEL,
  PLUS_MARKETING_LABEL,
  PLUS_MARKETING_STATES,
  marketToRegion,
  regionToMarket,
} from './settingsModel';

/**
 * The Settings surface (D-settings-s1, Phase 9 S1), the SINGLETON-CONFIG foundation.
 *
 * A settings PAGE (not a row-list CRUD): the current region's system config as an
 * edit form (site name + currency display + cashback default), the feature flags as
 * a per-flag toggle list, and the Plus marketing flag as a state selector. Config is
 * ONE ROW PER MARKET; the page edits the active region's row, and a GLOBAL admin can
 * apply the low-stakes site details to BOTH regions (two audited per-region writes,
 * behind the region-naming CrossRegionConfirm).
 *
 * The trust map (locked): site name + currency display are settings:write with
 * AUTOMATIC AUDIT only (the plain update, no reason); the cashback default is
 * settings:configure with a FORCED REASON (it affects what members earn); each
 * feature flag toggle is feature_flags:write with a FORCED REASON (the hard rule);
 * the Plus marketing flag is marketing_flags:write SUPER_ADMIN-only with a FORCED
 * REASON. The forced-reason writes reuse JustifiedAction verbatim (the reason rides
 * in the patch through the chokepoint into the audit diff).
 */

const CURRENCIES: readonly Currency[] = ['gbp', 'usd', 'eur'];

const SettingsInner = ({
  settings,
  flags,
  marketing,
}: {
  settings: SystemSetting[];
  flags: FeatureFlag[];
  marketing: PlusMarketingFlag[];
}) => {
  const dataProvider = useDataProvider<AdminDataProvider>();
  const notify = useNotify();
  const refresh = useRefresh();
  const { identity } = useGetIdentity();
  const { activeRegion } = useRegion();
  const { role, accessibleRegions } = useAccessContext();

  const email = String(identity?.email ?? identity?.id ?? 'unknown');
  const activeMarket = regionToMarket(activeRegion);

  const activeSetting =
    settings.find((s) => s.market === activeMarket) ?? settings[0];
  const activeMarketing =
    marketing.find((m) => m.market === activeMarket) ?? marketing[0];
  const marketFlags = flags.filter((f) => f.market === activeMarket);

  // The trust map, resolved once (locked capabilities).
  const canWriteSettings = hasAccess(
    { role, accessibleRegions },
    { capability: 'settings:write' },
  );
  const canConfigure = hasAccess(
    { role, accessibleRegions },
    { capability: 'settings:configure' },
  );
  const canWriteFlags = hasAccess(
    { role, accessibleRegions },
    { capability: 'feature_flags:write' },
  );
  const canMarketing = hasAccess(
    { role, accessibleRegions },
    { capability: 'marketing_flags:write' },
  );

  const [siteName, setSiteName] = useState(activeSetting?.siteName ?? '');
  const [currencyDisplay, setCurrencyDisplay] = useState<Currency>(
    activeSetting?.currencyDisplay ?? 'gbp',
  );
  const [cashbackDefault, setCashbackDefault] = useState(
    String(activeSetting?.cashbackDefault ?? ''),
  );
  const [targetState, setTargetState] = useState<PlusMarketingState>(
    activeMarketing?.state ?? 'dormant',
  );
  const [applyBoth, setApplyBoth] = useState(false);
  const [pendingCross, setPendingCross] = useState(false);
  const [busy, setBusy] = useState(false);

  // The regions a both-write covers, derived from the loaded (region-scoped) rows.
  const bothRegions = settings.map((s) => marketToRegion(s.market));
  const canApplyBoth = settings.length > 1;

  const nowIso = () => new Date().toISOString();

  // The low-stakes config write (site name + currency display): a plain audited
  // update, NO forced reason. One write per target market row.
  const writeSiteDetails = async (rows: SystemSetting[]) => {
    setBusy(true);
    try {
      for (const row of rows) {
        await dataProvider.update('systemSettings', {
          id: row.id,
          data: {
            siteName,
            currencyDisplay,
            updatedBy: email,
            updatedAt: nowIso(),
          },
          previousData: row,
        });
      }
      notify('Site details saved', { type: 'success' });
      setPendingCross(false);
      refresh();
    } catch {
      notify('Could not save the site details', { type: 'error' });
    } finally {
      setBusy(false);
    }
  };

  const onSaveSiteDetails = () => {
    if (applyBoth && canApplyBoth) {
      // A both-region change is confirmed (naming the regions) before committing.
      setPendingCross(true);
      return;
    }
    void writeSiteDetails(activeSetting ? [activeSetting] : []);
  };

  if (!activeSetting) {
    return (
      <Box sx={{ maxWidth: 820 }}>
        <Typography variant="h5" component="h1">
          Settings
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
          No configuration exists for this region yet.
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 820 }} data-testid="settings-page">
      <Typography variant="h5" component="h1" sx={{ mb: 1 }}>
        Settings
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Configuration for {marketToRegion(activeMarket)}. Changes are audited;
        flag and cashback changes record a reason.
      </Typography>

      {/* Section A: site details (low-stakes, settings:write, automatic audit). */}
      <Card sx={{ mb: 2 }} data-testid="settings-site">
        <CardContent>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            Site details
          </Typography>
          <Stack direction="row" spacing={2}>
            <TextField
              label="Site name"
              value={siteName}
              onChange={(e) => setSiteName(e.target.value)}
              margin="dense"
              size="small"
              fullWidth
              disabled={!canWriteSettings}
              inputProps={{ 'data-testid': 'settings-site-name' }}
            />
            <TextField
              select
              label="Currency display"
              value={currencyDisplay}
              onChange={(e) => setCurrencyDisplay(e.target.value as Currency)}
              margin="dense"
              size="small"
              sx={{ minWidth: 200 }}
              disabled={!canWriteSettings}
              slotProps={{ select: { native: true } }}
              inputProps={{ 'data-testid': 'settings-currency' }}
            >
              {CURRENCIES.map((c) => (
                <option key={c} value={c}>
                  {CURRENCY_LABEL[c]}
                </option>
              ))}
            </TextField>
          </Stack>
          {canApplyBoth && (
            <FormControlLabel
              control={
                <Switch
                  checked={applyBoth}
                  onChange={(e) => setApplyBoth(e.target.checked)}
                  slotProps={
                    {
                      // data-* is not in the input slot's typed props, but it
                      // renders on the checkbox input (the test target).
                      input: { 'data-testid': 'settings-apply-both' },
                    } as ComponentProps<typeof Switch>['slotProps']
                  }
                />
              }
              label="Apply the site details to both regions"
            />
          )}
          {canWriteSettings && (
            <Box sx={{ mt: 1 }}>
              <Button
                variant="contained"
                onClick={onSaveSiteDetails}
                disabled={busy}
                data-testid="settings-save-site"
              >
                Save site details
              </Button>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Section B: cashback default (settings:configure, forced reason). */}
      <Card sx={{ mb: 2 }} data-testid="settings-cashback">
        <CardContent>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            Cashback default
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            The default rate affects what members earn, so a change records a
            reason.
          </Typography>
          <Stack direction="row" spacing={2} alignItems="center">
            <TextField
              label="Default cashback rate (%)"
              value={cashbackDefault}
              onChange={(e) => setCashbackDefault(e.target.value)}
              type="number"
              margin="dense"
              size="small"
              sx={{ minWidth: 220 }}
              disabled={!canConfigure}
              inputProps={{ 'data-testid': 'settings-cashback-input' }}
            />
            {canConfigure && (
              <JustifiedAction
                resource="systemSettings"
                capability="settings:configure"
                id={activeSetting.id}
                label="Save cashback default"
                dialogTitle="Change the cashback default"
                dialogIntro="This is the default rate that affects what members earn. Record why."
                buildData={(reason) => ({
                  cashbackDefault: Number(cashbackDefault),
                  cashbackChangeReason: reason,
                  updatedBy: email,
                  updatedAt: nowIso(),
                })}
                previousData={activeSetting}
                successMessage="Cashback default saved"
              />
            )}
          </Stack>
        </CardContent>
      </Card>

      {/* Section C: feature flags (feature_flags:write, forced reason). */}
      <Card sx={{ mb: 2 }} data-testid="settings-flags">
        <CardContent>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            Feature flags
          </Typography>
          <Stack spacing={2}>
            {marketFlags.map((flag) => (
              <Stack
                key={flag.id}
                direction="row"
                spacing={2}
                alignItems="center"
                useFlexGap
                flexWrap="wrap"
              >
                <Box sx={{ minWidth: 220 }}>
                  <Typography variant="body2">{flag.label}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {flag.description}
                  </Typography>
                </Box>
                <Chip
                  size="small"
                  label={flag.enabled ? 'On' : 'Off'}
                  color={flag.enabled ? 'success' : 'default'}
                  variant={flag.enabled ? 'filled' : 'outlined'}
                  data-testid={`flag-chip-${flag.key}`}
                />
                {canWriteFlags && (
                  <JustifiedAction
                    resource="featureFlags"
                    capability="feature_flags:write"
                    id={flag.id}
                    label={flag.enabled ? 'Disable' : 'Enable'}
                    color={flag.enabled ? 'warning' : 'success'}
                    dialogTitle={`${flag.enabled ? 'Disable' : 'Enable'} ${flag.label}`}
                    dialogIntro="Flag changes are recorded with a reason."
                    buildData={(reason) => ({
                      enabled: !flag.enabled,
                      lastChangeReason: reason,
                      updatedBy: email,
                      updatedAt: nowIso(),
                    })}
                    previousData={flag}
                    successMessage="Flag updated"
                  />
                )}
              </Stack>
            ))}
          </Stack>
        </CardContent>
      </Card>

      {/* Section D: Plus marketing flag (marketing_flags:write, super_admin only). */}
      {activeMarketing && (
        <Card sx={{ mb: 2 }} data-testid="settings-marketing">
          <CardContent>
            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
              useFlexGap
              flexWrap="wrap"
            >
              <Typography variant="subtitle2">Plus marketing flag</Typography>
              <Chip
                size="small"
                label={PLUS_MARKETING_LABEL[activeMarketing.state]}
                data-testid="marketing-state-chip"
              />
            </Stack>
            <Typography variant="body2" color="text.secondary" sx={{ my: 1 }}>
              Flipping the Plus marketing state live is consequential, so this
              is a super_admin action and records a reason.
            </Typography>
            <Stack direction="row" spacing={2} alignItems="center">
              <TextField
                select
                label="Set state"
                value={targetState}
                onChange={(e) =>
                  setTargetState(e.target.value as PlusMarketingState)
                }
                margin="dense"
                size="small"
                sx={{ minWidth: 220 }}
                disabled={!canMarketing}
                slotProps={{ select: { native: true } }}
                inputProps={{ 'data-testid': 'marketing-state-select' }}
              >
                {PLUS_MARKETING_STATES.map((s) => (
                  <option key={s} value={s}>
                    {PLUS_MARKETING_LABEL[s]}
                  </option>
                ))}
              </TextField>
              {canMarketing && (
                <JustifiedAction
                  resource="plusMarketingFlags"
                  capability="marketing_flags:write"
                  id={activeMarketing.id}
                  label="Apply marketing state"
                  color="warning"
                  dialogTitle="Change the Plus marketing flag"
                  dialogIntro="This changes the Plus marketing state for the region platform-wide. Record why."
                  buildData={(reason) => ({
                    state: targetState,
                    lastChangeReason: reason,
                    updatedBy: email,
                    updatedAt: nowIso(),
                  })}
                  previousData={activeMarketing}
                  successMessage="Marketing flag updated"
                />
              )}
            </Stack>
          </CardContent>
        </Card>
      )}

      <CrossRegionConfirm
        isOpen={pendingCross}
        loading={busy}
        regions={bothRegions}
        what="site details"
        onConfirm={() => void writeSiteDetails(settings)}
        onClose={() => setPendingCross(false)}
      />
    </Box>
  );
};

/** The settings surface. Exported for testing without the access wrapper. */
export const SettingsView = () => {
  const { region, activeRegion } = useRegion();
  const listParams = {
    pagination: { page: 1, perPage: 100 },
    sort: { field: 'market', order: 'ASC' as const },
    filter: { region },
  };
  const settings = useGetList<SystemSetting>('systemSettings', listParams);
  const flags = useGetList<FeatureFlag>('featureFlags', listParams);
  const marketing = useGetList<PlusMarketingFlag>(
    'plusMarketingFlags',
    listParams,
  );

  if (settings.isLoading || flags.isLoading || marketing.isLoading) {
    return <Loading />;
  }
  return (
    <SettingsInner
      key={activeRegion}
      settings={settings.data ?? []}
      flags={flags.data ?? []}
      marketing={marketing.data ?? []}
    />
  );
};

/** Gated entry point mounted at /settings. Editor and super_admin reach it. */
export const SettingsPage = () => (
  <RequireAccess capability="settings:write">
    <SettingsView />
  </RequireAccess>
);
